import React, { useState, useContext, useEffect } from "react";
import {
  Row,
  Col,
  Card,
  Button,
  Avatar,
  Modal,
  Form,
  Input,
  Upload as UploadAntd,
  InputNumber,
  DatePicker,
  Select,
} from "antd";
import profilavatar from "../assets/images/face-1.jpg";
import Main from "../components/layout/Main";
import useRequest from "../hooks/useRequest";
import { ShowToast, Severty } from "../helper/toast";
import { AuthContext } from "../context/AuthContext";
import { ExclamationCircleOutlined, UploadOutlined } from "@ant-design/icons";
import axios from "axios";
import apiPath from "../constants/apiPath";
import moment from "moment";
import dayjs from "dayjs";
// Uploads now go through the backend (POST /admin/media/upload), which
// holds the real AWS credentials server-side and never exposes them. This
// used to upload straight to S3 from the browser with a hardcoded access
// key + secret baked into this file — visible to anyone via view-source on
// the deployed site, no login required. See MediaUploadController.ts.
function Profile() {
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState({});
  const { logout } = useContext(AuthContext);
  const [visible, setVisible] = useState(false);
  const [profileVisible, setProfileVisible] = useState(false);
  const [selected, setSelected] = useState();
  const [refresh, setRefresh] = useState(false);
  const [commission, setCommission] = useState();
  const [nextPayoutDate, setNextPayoutDate] = useState();
  const [payoutFrequency, setPayoutFrequency] = useState();

  const [desCommission, setDesCommission] = useState();
  const [desNextPayoutDate, setDesNextPayoutDate] = useState();
  const [desPayoutFrequency, setDesPayoutFrequency] = useState();

  // New states for Minimum Amount & Payment Methods
  const [minimumAmount, setMinimumAmount] = useState();
  const [paymentMethods, setPaymentMethods] = useState([]);

  const [form] = Form.useForm();
  const { request } = useRequest();
  const { confirm } = Modal;

  useEffect(() => {
    request({
      url: "/admin/auth/get-profile",
      method: "GET",
      onSuccess: (data) => {
        console.log(data);
        setProfile(data.data);
        setSelected(data.data);
        setCommission(data.data?.vendor_commission || 0);
        setPayoutFrequency(data.data?.payoutFrequency || 0);
        setNextPayoutDate(data.data?.nextPayoutDate || "");

        setDesCommission(data.data?.designer_commission || 0);
        setDesPayoutFrequency(data.data?.desPayoutFrequency || 0);
        setDesNextPayoutDate(data.data?.desNextPayoutDate || "");

        // Set Minimum Amount & Payment Methods
        setMinimumAmount(data.data?.minimum_amount || 0);
        setPaymentMethods(data.data?.paymentMethods || []);
      },
      onError: (error) => {
        console.log(error);
        ShowToast(error, Severty.ERROR);
      },
    });
  }, [refresh]);

  const resetData = () => {
    request({
      url: "/common/factory-reset",
      method: "GET",
      onSuccess: (data) => {
        console.log(data);
        setProfile(data.data);
        setSelected(data.data);
      },
      onError: (error) => {
        console.log(error);
        ShowToast(error, Severty.ERROR);
      },
    });
  };

  const showConfirm = () => {
    setTimeout(() => {
      confirm({
        icon: <ExclamationCircleOutlined />,
        content: <Button>Are you sure you want to logout?</Button>,
        onOk() {
          logout();
        },
        onCancel() {
          console.log("Cancel");
        },
      });
    }, 5);
  };

  const showFactoryData = () => {
    setTimeout(() => {
      confirm({
        icon: <ExclamationCircleOutlined />,
        content: (
          <Button>Are you sure you want to Reset all the data?</Button>
        ),
        onOk() {
          resetData();
        },
        onCancel() {
          console.log("Cancel");
        },
      });
    }, 5);
  };

  const updateCommission = (values) => {
    const payload = {
      ...values,
      minimum_amount: minimumAmount,
      paymentMethods: paymentMethods,
    };

    request({
      url: apiPath.setCommission,
      method: "POST",
      data: payload,
      onSuccess: (data) => {
        if (data?.statusText === "SUCCESS") {
          ShowToast(data?.message, Severty.SUCCESS);
        } else {
          ShowToast(data?.message, Severty.ERROR);
        }
      },
      onError: (err) => {
        ShowToast(err?.response?.data?.message, Severty.ERROR);
      },
    });
  };

  return (
    <>
      <Card
        className="card-profile-head"
        bodyStyle={{ display: "none" }}
        title={
          <Row justify="space-between" align="middle" gutter={[24]}>
            <Col span={24} md={24}>
              <Avatar.Group>
                <Avatar
                  size={74}
                  shape="square"
                  src={profile ? profile.profilePic : profilavatar}
                />
                <div className="avatar-info">
                  <h4 className="font-semibold m-0">
                    {profile ? profile.name : ""}
                  </h4>
                  <p>{profile ? profile.email : ""}</p>
                </div>
              </Avatar.Group>
            </Col>
          </Row>
        }
      ></Card>

      <Card className="profile-nav">
        <div className="profile-nav-inner">
          <Button onClick={() => setProfileVisible(true)}>Edit </Button>
          <Button onClick={() => setVisible(true)}>Change Password</Button>
          <Button onClick={() => showConfirm()}>Logout</Button>
        </div>
      </Card>

      <SetCommission
        payoutFrequency={payoutFrequency}
        commission={commission}
        nextPayoutDate={nextPayoutDate}
        desPayoutFrequency={desPayoutFrequency}
        desCommission={desCommission}
        desNextPayoutDate={desNextPayoutDate}
        minimumAmount={minimumAmount} // Pass Minimum Amount
        paymentMethods={paymentMethods} // Pass Payment Methods
        setMinimumAmount={setMinimumAmount} // Allow updating state
        setPaymentMethods={setPaymentMethods} // Allow updating state
        updateCommission={updateCommission}
      />

      {profileVisible && (
        <EditProfile
          show={profileVisible}
          hide={() => setProfileVisible(false)}
          data={selected}
          refresh={() => setRefresh((prev) => !prev)}
        />
      )}

      {visible && (
        <ChangePassword
          show={visible}
          hide={() => setVisible(false)}
          refresh={() => setRefresh((prev) => !prev)}
        />
      )}
    </>
  );
}

const EditProfile = ({ show, hide, data, refresh }) => {
  const [form] = Form.useForm();
  const { request } = useRequest();
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState([]);

  const beforeUpload = (file) => {
    // console.log(file.type, "file type>>>>>>.")
    // if (fileType.includes(file.type)) {

    // } else {
    //   message.error("File format is not correct")
    //   return false
    // }
    const isLt5M = file.size / 1024 / 1024 < 5;

    if (!isLt5M) {
      // ShowToast.error(`Image must be smaller than 5 MB!`)
      return false;
    }
    return true;
  };

  const uploadFileToS3 = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    const res = await axios.post(`${apiPath.baseURL}admin/media/upload`, formData, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
        "Content-Type": "multipart/form-data",
      },
    });
    return { Location: res.data?.data?.url };
  };

  const handleChange = async (event) => {
    const { file } = event;
  
    let validateImage =
      file.type.includes("image") && !file.type.includes("svg") && !file.type.includes("gif");
  
    if (!validateImage) {
      return ShowToast("Media not supported " + file.type, Severty.ERROR);
    }
  
    const isValidSize = beforeUpload(file);
    if (!isValidSize) {
      return ShowToast("Image must be smaller than 5 MB!", Severty.ERROR);
    }
  
    setFile([{ uid: file.uid, name: file.name, status: "uploading" }]); // ✅ Ensure `uid` exists
  
    try {
      const uploadResponse = await uploadFileToS3(file);
  
      if (!uploadResponse || !uploadResponse.Location) {
        throw new Error("Upload failed");
      }
  
      setFile([
        {
          uid: file.uid, // ✅ Ensure `uid` exists
          name: file.name,
          status: "done",
          url: uploadResponse.Location, // ✅ Correct file URL
          thumbUrl: uploadResponse.Location, // ✅ Ensure preview works
        },
      ]);
  
      console.log(uploadResponse);
    } catch (err) {
      console.error(err);
      setFile([]); // ✅ Reset file if upload fails
    }
  };
  
  useEffect(() => {
    if (!data) return;
  
    form.setFieldsValue({ ...data });
  
    if (data.profilePic) {
      setFile([
        {
          uid: "1", // ✅ Ensure `uid` exists
          name: "profile.jpg",
          status: "done",
          url: data.profilePic,
          thumbUrl: data.profilePic,
        },
      ]);
    } else {
      setFile([]);
    }
  }, [data]);
  const onEditProfile = (values) => {
    const { email, name } = values;
    if (file.length <= 0)
      return ShowToast("Please select the profile Image ", Severty.ERROR);
    const payload = {};
    setLoading(true);
    payload.email = email;
    payload.name = name;
    payload.profilePic = file.length > 0 ? file[0].url : null;
    request({
      url: "/admin/auth/update-profile",
      method: "POST",
      data: payload,
      onSuccess: (data) => {
        setLoading(false);
        if (data.status) {
          ShowToast(data.message, Severty.SUCCESS);
          hide();
          refresh();
          // hide()
        } else {
          ShowToast(data.message, Severty.ERROR);
        }
      },
      onError: (error) => {
        ShowToast(error.response.data.message, Severty.ERROR);
        // console.log(error.response.data, "Error")
        setLoading(false);
      },
    });
  };
  return (
    <Modal
      visible={show}
      title={`${data ? "Edit Profile" : ""}`}
      okText="Ok"
      onCancel={hide}
      okButtonProps={{
        form: "create",
        htmlType: "submit",
        loading: loading,
      }}
    >
      <Form id="create" form={form} onFinish={onEditProfile} layout="vertical">
        <Row>
          <Col span={24}>
           <Form.Item
              label="Name"
              name="name"
              rules={[
                { required: true, message: "Please input the name!" },
                {
                  validator: (_, value) => {
                    if (value && value.trim() === "") {
                      return Promise.reject("Name cannot be only spaces");
                    }
                    return Promise.resolve();
                  },
                },
                {
                  pattern: /^[a-zA-Z ]*$/,
                  message: "Only alphabetic characters allowed",
                },
                {
                  max: 50,
                  message: "Name cannot exceed 50 characters",
                },
              ]}
            >
              <Input placeholder="Name" />
            </Form.Item>

          </Col>
          <Col span={24}>
            <Form.Item
              label="Email"
              name="email"
              rules={[
                {
                  type: "email",
                  message: "The input is not valid E-mail!",
                },
                { required: true, message: "Please input the email!" },
                {
                  max: 100,
                  message: "Email cannot exceed 100 characters",
                },
              ]}
            >
              <Input placeholder={"Email"} />
            </Form.Item>
          </Col>
          <Col span={24}>
          <UploadAntd
            listType="picture"
            maxCount={1}
            customRequest={handleChange}
            // onChange={handleChange}
            onRemove={() => setFile([])} // ✅ Ensure file state resets
            fileList={file} // ✅ Ensure it always contains valid objects
          >
            {file.length > 0 ? null : <Button icon={<UploadOutlined />}>Upload</Button>}
          </UploadAntd>
            <p className="main-new-upload-graph-name-insturction">
              **Logo should be (300-800)X(300-800) for best view in profile
              image. You can select only ( .png, .jpg) format files upto 5 MB
              file size..!!!
            </p>
          </Col>
        </Row>

        {/* <Form.Item name="description" label="Description">
            <Input type="textarea" />
          </Form.Item> */}

        {/* <Form.Item name="type" label="Type">
            <Radio.Group>
              <Radio value="public">Public</Radio>
              
              <Radio value="private">Private</Radio>
            </Radio.Group>
          </Form.Item> */}
      </Form>
    </Modal>
  );
};

const ChangePassword = ({ show, hide }) => {
  const [form] = Form.useForm();
  const { request } = useRequest();
  const { logout } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);

  const handleCreate = (values) => {
    const { old_password, new_password } = values;
    const payload = {};
    if (!old_password || !new_password)
      return ShowToast("Please enter password ", Severty.ERROR);
    setLoading(true);
    payload.new_password = new_password;
    payload.old_password = old_password;
    request({
      url: "/admin/auth/change-password",
      method: "POST",
      data: payload,
      onSuccess: (data) => {
        setLoading(false);
        if (data.status) {
          ShowToast(data.message, Severty.SUCCESS);
          hide();
          logout();
        } else {
          ShowToast(data.message, Severty.ERROR);
        }
      },
      onError: (error) => {
        ShowToast(error.response.data.message, Severty.ERROR);
        // console.log(error.response.data, "Error")
        setLoading(false);
      },
    });
  };

  return (
    <Modal
      open={show}
      title="Change password"
      okText="Ok"
      onCancel={() => {
        hide();
        form.resetFields();
      }}
      okButtonProps={{
        form: "create",
        htmlType: "submit",
        loading: loading,
      }}
    >
      <Form
        id="create"
        form={form}
        layout="vertical"
        onFinish={handleCreate}
        className="change-pw"
        disabled={loading}
      >
        <Form.Item
          label="Old Password"
          name="old_password"
          hasFeedback
          rules={[
            { required: true, message: "Please input the old password!" },
          ]}
        >
          <Input.Password maxLength={50} placeholder={"Old Password"} />
        </Form.Item>
        <Form.Item
          label="New Password"
          name="new_password"
          hasFeedback
          rules={[
            { required: true, message: "Please input the new password!" },
            {
              min: 8,
              message: "Password must be at least 8 characters long!",
            },
            {
              pattern: /^(?=.*[A-Z])(?=.*\d)(?=.*[a-z]).+$/,
              message:
                "Password must contain at least one uppercase letter, one number, and one lowercase letter!",
            },
            {
              max: 100,
              message: "Password cannot exceed 100 characters",
            },
          ]}
        >
          <Input.Password maxLength={50} placeholder={"New Password"} />
        </Form.Item>
        <Form.Item
          label="Confirm New Password"
          name="confirm_new_password"
          dependencies={["new_password"]}
          hasFeedback
          rules={[
            { required: true, message: "Please input the confirm password!" },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue("new_password") === value) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error("Passwords that you entered do not match!")
                );
              },
            }),
          ]}
        >
          <Input.Password maxLength={50} placeholder={"Confirm New Password"} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const SetCommission = ({
  commission,
  payoutFrequency,
  nextPayoutDate,
  desCommission,
  desPayoutFrequency,
  desNextPayoutDate,
  minimumAmount, // ✅ Ensure this is received
  paymentMethods, // ✅ Fix the naming here
  setMinimumAmount, // ✅ Ensure the setter function is received
  setPaymentMethods, // ✅ Ensure the setter function is received
  updateCommission,
}) => {
  const [form] = Form.useForm();
console.log(">>>>>>>>>",setPaymentMethods);
  useEffect(() => {
    form.setFieldsValue({
      vendor_commission: commission ?? 0,
      payoutFrequency: payoutFrequency ?? 0,
      nextPayoutDate: nextPayoutDate ? moment(nextPayoutDate) : "",

      designer_commission: desCommission?? 0,
      desPayoutFrequency: desPayoutFrequency?? 0,
      desNextPayoutDate: desNextPayoutDate
       ? moment(desNextPayoutDate)
        : "",
        minimum_amount: Number(minimumAmount) ?? 0,
    paymentMethods: paymentMethods ?? [], // Ensure it is an array
    });
  }, [commission, payoutFrequency, nextPayoutDate, 
    desCommission, desPayoutFrequency, desNextPayoutDate]);

  const payoutOptions = [
    {
      label: "15 Days",
      value: 15,
    },
    {
      label: "1 Month",
      value: 30,
    },
    {
      label: "1 Year",
      value: 365,
    },
  ];

  const paymentOptions = [
    {
      label: "Bank Transfer",
      value: "bank_transfer",
    },
    {
      label: "Digital Wallet",
      value: "digital_wallet",
    },
  ];

  return (
    <>


    </>
  );
};

export default Profile;
